// ScrollSpy - highlights the in-page nav link (and its parent <li>) whose
// section is currently scrolled into view. Behavioral port of v1
// modules/ScrollSpy.js; the HTML contract ([data-gumshoe] a, is-active,
// data-scroll-offset) is unchanged.
//
// v1 delegated the scroll math to a fork of gumshoe 3.5 (archived upstream,
// like tippy -> floating-ui the vendor is replaced by our own code). The
// algorithm mirrors gumshoe: distances of all targets from the document top
// (minus a fixed [data-gumshoe-header] and the per-link offset) sorted
// furthest-first, activate the first one above the scroll position, last
// section wins at the very bottom of the page.
//
// v1 bug fixed: destroy() left the document-level `toggle` listener attached
// forever; here it is removed.

import { TOGGLE_EVT } from './toggle';
import { resolveScrollOffset, type ScrollOffset } from '../utils';

export interface ScrollSpyConfig {
  activeClass: string;
  selectorHeader: string;
  offset: ScrollOffset;
}

interface NavItem {
  nav: HTMLAnchorElement;
  target: HTMLElement;
  parent: Element | null;
  distance: number;
}

const defaultConfig: ScrollSpyConfig = {
  activeClass: 'is-active',
  selectorHeader: '[data-gumshoe-header]',
  offset: 0,
};

// Events are throttled to ~15fps, same as gumshoe.
const THROTTLE_MS = 66;

const getHeight = (el: HTMLElement): number =>
  Math.max(el.scrollHeight, el.offsetHeight, el.clientHeight);

const getDocumentHeight = (): number =>
  Math.max(
    document.body.scrollHeight, document.documentElement.scrollHeight,
    document.body.offsetHeight, document.documentElement.offsetHeight,
    document.body.clientHeight, document.documentElement.clientHeight,
  );

const getOffsetTop = (el: HTMLElement): number => {
  let location = 0;
  let current: HTMLElement | null = el;

  if (el.offsetParent) {
    while (current) {
      location += current.offsetTop;
      current = current.offsetParent as HTMLElement | null;
    }
  } else {
    location = el.offsetTop;
  }

  return location;
};

const isInViewport = (el: Element): boolean => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export default class ScrollSpy {
  selector: string;

  config: ScrollSpyConfig;

  navs: NavItem[] = [];

  private header: HTMLElement | null = null;

  private headerHeight = 0;

  private docHeight = 0;

  private currentNav: NavItem | null = null;

  private throttleTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(selector = '[data-gumshoe] a', config: Partial<ScrollSpyConfig> = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  update = (): void => {
    this.destroy();
    this._init();
  };

  destroy = (): void => {
    window.removeEventListener('scroll', this._handleScroll);
    window.removeEventListener('resize', this._handleResize);
    document.removeEventListener(TOGGLE_EVT, this._handleToggle);

    if (this.throttleTimeout) {
      clearTimeout(this.throttleTimeout);
      this.throttleTimeout = null;
    }

    this.navs = [];
    this.header = null;
    this.currentNav = null;
  };

  /** Recalculate target distances (call after content height changes). */
  setDistances = (): void => {
    this.docHeight = getDocumentHeight();
    this.headerHeight = this.header ? getHeight(this.header) : 0;

    this.navs.forEach(item => {
      const distance =
        getOffsetTop(item.target) -
        this.headerHeight -
        resolveScrollOffset(item.nav, this.config.offset, item.target);

      item.distance = Math.max(distance, 0);
    });

    // Furthest from the top first, so the first match below wins.
    this.navs.sort((a, b) => b.distance - a.distance);
  };

  /** Determine and activate the nav for the current scroll position. */
  getCurrentNav = (): NavItem | null => {
    const furthest = this.navs[0];

    if (!furthest) {
      return null;
    }

    const position = window.pageYOffset;

    // At the bottom of the page activate the last section once it shows.
    if (
      window.innerHeight + position >= this.docHeight &&
      isInViewport(furthest.target)
    ) {
      this._activateNav(furthest);
      return furthest;
    }

    const active = this.navs.find(item => item.distance <= position);

    if (active) {
      this._activateNav(active);
      return active;
    }

    this._deactivateCurrentNav();
    return null;
  };

  private _init = (): void => {
    this.header = document.querySelector<HTMLElement>(this.config.selectorHeader);

    const navs: NavItem[] = [];

    document.querySelectorAll<HTMLAnchorElement>(this.selector).forEach(nav => {
      if (!nav.hash) return;

      const target = document.querySelector<HTMLElement>(nav.hash);
      if (!target) return;

      navs.push({
        nav,
        target,
        parent: nav.parentElement?.tagName.toLowerCase() === 'li' ? nav.parentElement : null,
        distance: 0,
      });
    });

    this.navs = navs;

    if (!this.navs.length) {
      return;
    }

    // Adopt a nav pre-activated by the server as the current one.
    const preActive = this.navs.find(item =>
      item.nav.classList.contains(this.config.activeClass),
    );
    this.currentNav = preActive ?? null;

    this.setDistances();
    this.getCurrentNav();

    window.addEventListener('scroll', this._handleScroll);
    window.addEventListener('resize', this._handleResize);
    // Toggled sections (collapse, tabs) change the layout under the spy.
    document.addEventListener(TOGGLE_EVT, this._handleToggle);
  };

  private _activateNav = (item: NavItem): void => {
    this._deactivateCurrentNav();

    item.nav.classList.add(this.config.activeClass);
    item.parent?.classList.add(this.config.activeClass);

    this.currentNav = item;
  };

  private _deactivateCurrentNav = (): void => {
    if (this.currentNav) {
      this.currentNav.nav.classList.remove(this.config.activeClass);
      this.currentNav.parent?.classList.remove(this.config.activeClass);
      this.currentNav = null;
    }
  };

  private _throttle = (fn: () => void): void => {
    if (this.throttleTimeout) {
      return;
    }

    this.throttleTimeout = setTimeout(() => {
      this.throttleTimeout = null;
      fn();
    }, THROTTLE_MS);
  };

  private _handleScroll = (): void => {
    this._throttle(() => {
      this.getCurrentNav();
    });
  };

  private _handleResize = (): void => {
    this._throttle(() => {
      this.setDistances();
      this.getCurrentNav();
    });
  };

  private _handleToggle = (): void => {
    this.setDistances();
  };
}
